import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { resolveActivityType, PIN_ICON_STROKE, FLIK_FALLBACK } from '../../activities/activityTypes';
import { flikMarkImgHtml } from '../Logo/flikMark';
import './RouteMap.css';

/**
 * OpenStreetMap-backed map for the private home.
 *
 * Built on vanilla Leaflet (not react-leaflet) on purpose: CRA's Jest resolver
 * chokes on packages that expose ESM-only `exports` subpaths (same class of
 * problem that ruled out react-router v7), and vanilla Leaflet sidesteps it.
 * All markers are HTML `divIcon`s so we never depend on Leaflet's bundled PNG
 * marker assets (whose paths break under webpack).
 *
 * The map is NOT created on mount: we first resolve the geolocation decision.
 *  - permission already granted → locate and open the map on the user.
 *  - permission still "prompt"  → show a gate; the map opens once the user
 *    answers the browser prompt (accept → their position, reject → fallback).
 *  - permission already denied  → open the map on the fallback right away.
 * The fallback is the Ciudad de las Artes y las Ciencias (Valencia). Satellite
 * imagery is the default layer.
 */

// Ciudad de las Artes y las Ciencias, València — the fallback centre used when
// the user declines (or can't share) their location.
const FALLBACK_CENTER = [39.4547, -0.3524];
const DEFAULT_ZOOM = 13;

// Satellite is the default layer.
const INITIAL_LAYER = 'satelite';

// Tile sources. "Relieve" is the plain OpenStreetMap raster; the other two are
// OSM-derived / imagery alternates.
const LAYERS = {
  relieve: {
    label: 'Relieve',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19,
  },
  satelite: {
    label: 'Satélite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri, Maxar, Earthstar Geographics',
    maxZoom: 19,
  },
  topografico: {
    label: 'Topográfico',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map data: &copy; OpenStreetMap, SRTM | &copy; OpenTopoMap (CC-BY-SA)',
    maxZoom: 17,
  },
};

const LAYER_ORDER = ['relieve', 'satelite', 'topografico'];

const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 };

const userIcon = L.divIcon({
  className: 'flik-user-marker',
  html: '<span class="flik-user-marker__pulse"></span><span class="flik-user-marker__dot"></span>',
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

// Builds the "pin de cota": a wedge with a bevelled top-right corner (the FLIK
// snow-cut) in the activity's colour, with its icon in roca-900 on top. The
// point (bottom tip) is the anchor so it sits exactly on the coordinate. The
// clip-path wedge is a CSS class; the colour and icon vary per type.
// Cluster pin ("cúmulo"): a cream square with the FLIK snow-cut bevel and the
// count in mono. It has no wedge point — a cluster doesn't mark one coordinate.
// It grows with the number of activities it holds so denser groups read louder.
function clusterIcon(count) {
  const size = count >= 100 ? 52 : count >= 10 ? 46 : 40;
  return L.divIcon({
    className: 'flik-cluster',
    html: `<span class="flik-cluster__box">${count}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// Distance in pixels below which two markers are folded into a cluster at the
// current zoom. Roughly the pin footprint plus breathing room.
const CLUSTER_RADIUS = 56;

function pinIcon(activityType, selected = false) {
  const type = resolveActivityType(activityType);
  // The FLIK fallback is the brand PNG (gold) on its dark pin body; every other
  // type is a stroked line icon in roca-900 over its flat pin colour.
  const icon =
    type.key === FLIK_FALLBACK.key
      ? flikMarkImgHtml(20, PIN_ICON_STROKE)
      : `<svg width="20" height="20" viewBox="0 0 24 24"><path d="${type.path}"` +
        ` fill="none" stroke="${PIN_ICON_STROKE}" stroke-width="1.75"` +
        ` stroke-linecap="butt" stroke-linejoin="miter"></path></svg>`;
  return L.divIcon({
    className: `flik-pin${selected ? ' flik-pin--selected' : ''}`,
    html:
      `<span class="flik-pin__body" style="background:${type.color}">${icon}</span>`,
    iconSize: selected ? [44, 56] : [36, 46],
    iconAnchor: selected ? [22, 56] : [18, 46],
    popupAnchor: [0, selected ? -56 : -46],
  });
}

const RouteMap = forwardRef(function RouteMap(
  { activities = [], selectedId = null, onBoundsChange, onActivitySelect } = {},
  ref
) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const tileRef = useRef(null);
  const userMarkerRef = useRef(null);
  // Layer group holding the activity markers. Cleared and refilled whenever the
  // parent hands down a new set (the parent fetches by the current viewport, so
  // the map only shows what the current area — and zoom — covers).
  const activitiesLayerRef = useRef(null);
  const activitiesDebounceRef = useRef(null);
  // Latest activity set kept so zoom changes can re-cluster without waiting for
  // the parent to hand down a fresh list.
  const activitiesRef = useRef(activities);
  activitiesRef.current = activities;
  // Latest callback kept in a ref so the map's long-lived event handlers always
  // reach the current one instead of the closure captured at map creation.
  const onBoundsChangeRef = useRef(onBoundsChange);
  onBoundsChangeRef.current = onBoundsChange;
  const onActivitySelectRef = useRef(onActivitySelect);
  onActivitySelectRef.current = onActivitySelect;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const [activeLayer, setActiveLayer] = useState(INITIAL_LAYER);
  const [locating, setLocating] = useState(false);

  // 'init'    → deciding whether to show the gate (checking permission)
  // 'prompt'  → waiting for the user to allow/skip location
  // 'locating'→ browser prompt open / fetching the position
  // 'ready'   → map created and visible
  const [phase, setPhase] = useState('init');

  /* ------------------------------ map setup --------------------------- */

  // Creates the Leaflet map centred on `center`, drops the sample POIs and a
  // "you are here" marker, then reveals it. Guards against StrictMode's double
  // invocation and against a center resolving after unmount.
  function createMap(center) {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      center,
      zoom: DEFAULT_ZOOM,
      zoomControl: false, // we render our own controls to match the design
      attributionControl: true,
    });
    mapRef.current = map;

    const initial = LAYERS[INITIAL_LAYER];
    tileRef.current = L.tileLayer(initial.url, {
      attribution: initial.attribution,
      maxZoom: initial.maxZoom,
    }).addTo(map);

    // Nudge the attribution into a compact, on-brand corner.
    map.attributionControl.setPrefix('');

    // Empty layer group for the activities. Nothing is shown until the parent
    // hands down the first set — the map starts clean.
    activitiesLayerRef.current = L.layerGroup().addTo(map);

    // Report the viewport whenever it settles (pan or zoom) so the parent can
    // re-query. A short debounce coalesces the burst of events per gesture.
    map.on('moveend zoomend', scheduleBoundsReport);
    // Re-cluster immediately on zoom: the pixel distances between markers change
    // with the zoom level, so groups split apart / merge as you zoom in and out.
    map.on('zoomend', () => renderActivities(activitiesRef.current));

    // Mark the centre as "you are here" (real position or fallback).
    placeUserMarker(center);

    setPhase('ready');

    // Report the initial viewport and draw whatever the parent already holds.
    reportBounds();
    renderActivities(activities);

    // Leaflet occasionally needs a nudge once its flex container has sized.
    setTimeout(() => {
      if (mapRef.current === map) map.invalidateSize();
    }, 200);
  }

  /* ---------------------------- activities ---------------------------- */

  // Debounced trigger bound to moveend/zoomend.
  function scheduleBoundsReport() {
    if (activitiesDebounceRef.current) clearTimeout(activitiesDebounceRef.current);
    activitiesDebounceRef.current = setTimeout(reportBounds, 250);
  }

  // Hands the current viewport to the parent as the four-corner polygon the
  // backend filters by (NW, NE, SE, SW). Zooming in/out changes these corners,
  // so the parent's re-query narrows or widens the returned set automatically.
  function reportBounds() {
    const map = mapRef.current;
    if (!map || !onBoundsChangeRef.current) return;
    const b = map.getBounds();
    onBoundsChangeRef.current([
      [b.getNorth(), b.getWest()],
      [b.getNorth(), b.getEast()],
      [b.getSouth(), b.getEast()],
      [b.getSouth(), b.getWest()],
    ]);
  }

  // Folds activities that sit within CLUSTER_RADIUS pixels of each other (at the
  // current zoom) into buckets, using a simple pixel-grid sweep. The selected
  // activity is never clustered — it always keeps its own pin. Returns a list of
  // { items } buckets (single-item buckets are plain pins).
  function clusterActivities(list, map) {
    const points = [];
    (list || []).forEach((a) => {
      const lat = Number(a?.values?.latitude);
      const lng = Number(a?.values?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      points.push({ a, lat, lng, pt: map.latLngToLayerPoint([lat, lng]) });
    });

    const buckets = [];
    const claimed = new Array(points.length).fill(false);
    for (let i = 0; i < points.length; i += 1) {
      if (claimed[i]) continue;
      const p = points[i];
      const isSelected = p.a?.id != null && p.a.id === selectedIdRef.current;
      if (isSelected) {
        buckets.push({ items: [p], selected: true });
        claimed[i] = true;
        continue;
      }
      const group = [p];
      claimed[i] = true;
      for (let j = i + 1; j < points.length; j += 1) {
        if (claimed[j]) continue;
        const q = points[j];
        if (q.a?.id != null && q.a.id === selectedIdRef.current) continue;
        if (p.pt.distanceTo(q.pt) <= CLUSTER_RADIUS) {
          group.push(q);
          claimed[j] = true;
        }
      }
      buckets.push({ items: group, selected: false });
    }
    return buckets;
  }

  function renderActivities(list) {
    const layer = activitiesLayerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;
    layer.clearLayers();

    clusterActivities(list, map).forEach((bucket) => {
      // Single activity (or the selected one) → the "pin de cota".
      if (bucket.items.length === 1) {
        const { a, lat, lng } = bucket.items[0];
        const marker = L.marker([lat, lng], {
          icon: pinIcon(a.activityType, bucket.selected),
          keyboard: false,
          zIndexOffset: bucket.selected ? 800 : 0,
        });
        marker.on('click', () => {
          if (onActivitySelectRef.current) onActivitySelectRef.current(a);
        });
        marker.addTo(layer);
        return;
      }

      // Several nearby activities → a "cúmulo" placed at their average position.
      const avgLat =
        bucket.items.reduce((s, p) => s + p.lat, 0) / bucket.items.length;
      const avgLng =
        bucket.items.reduce((s, p) => s + p.lng, 0) / bucket.items.length;
      const marker = L.marker([avgLat, avgLng], {
        icon: clusterIcon(bucket.items.length),
        keyboard: false,
        zIndexOffset: 400,
      });
      // Clicking a cluster zooms into it so the individual pins spread apart.
      marker.on('click', () => {
        const bounds = L.latLngBounds(bucket.items.map((p) => [p.lat, p.lng]));
        map.fitBounds(bounds, { padding: [64, 64], maxZoom: map.getMaxZoom() });
      });
      marker.addTo(layer);
    });
  }

  // Redraw the markers whenever the parent hands down a new set.
  useEffect(() => {
    renderActivities(activities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities, selectedId]);

  // On mount, decide whether we can open the map straight away or must ask.
  useEffect(() => {
    let alive = true;

    if (!('geolocation' in navigator)) {
      createMap(FALLBACK_CENTER);
      return undefined;
    }

    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((res) => {
          if (!alive) return;
          if (res.state === 'granted') {
            requestLocation(); // already trusted us — locate immediately
          } else if (res.state === 'denied') {
            createMap(FALLBACK_CENTER); // already refused — go to the fallback
          } else {
            setPhase('prompt'); // undecided — show the gate
          }
        })
        .catch(() => {
          if (alive) setPhase('prompt');
        });
    } else {
      // No Permissions API: require an explicit gesture via the gate.
      setPhase('prompt');
    }

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tear the map down on unmount.
  useEffect(() => {
    return () => {
      if (activitiesDebounceRef.current) clearTimeout(activitiesDebounceRef.current);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        tileRef.current = null;
        userMarkerRef.current = null;
        activitiesLayerRef.current = null;
      }
    };
  }, []);

  /* ------------------------------ location ---------------------------- */

  // Requests the position and opens the map: on success at the user, on
  // denial/failure at the Valencia fallback. Used both for the initial gate and
  // for the "granted" fast path.
  function requestLocation() {
    if (!('geolocation' in navigator)) {
      createMap(FALLBACK_CENTER);
      return;
    }
    setPhase('locating');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        createMap([pos.coords.latitude, pos.coords.longitude]);
        setLocating(false);
      },
      () => {
        createMap(FALLBACK_CENTER);
        setLocating(false);
      },
      GEO_OPTIONS
    );
  }

  function skipLocation() {
    createMap(FALLBACK_CENTER);
  }

  function placeUserMarker(latlng) {
    const map = mapRef.current;
    if (!map) return;
    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng(latlng);
    } else {
      userMarkerRef.current = L.marker(latlng, {
        icon: userIcon,
        zIndexOffset: 1000,
        keyboard: false,
      }).addTo(map);
    }
  }

  // Re-centre on the live position (the crosshair button, once the map exists).
  function recenter() {
    const map = mapRef.current;
    if (!map || !('geolocation' in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (mapRef.current !== map) return;
        const latlng = [pos.coords.latitude, pos.coords.longitude];
        placeUserMarker(latlng);
        map.setView(latlng, DEFAULT_ZOOM, { animate: true });
        setLocating(false);
      },
      () => {
        if (mapRef.current !== map) return;
        setLocating(false);
      },
      GEO_OPTIONS
    );
  }

  /* ------------------------------ layers ------------------------------ */

  function switchLayer(key) {
    const map = mapRef.current;
    if (!map || key === activeLayer) return;
    const next = LAYERS[key];
    if (tileRef.current) map.removeLayer(tileRef.current);
    tileRef.current = L.tileLayer(next.url, {
      attribution: next.attribution,
      maxZoom: next.maxZoom,
    }).addTo(map);
    setActiveLayer(key);
  }

  // Let the parent (the mobile "M" button lives in HomePage's search row)
  // cycle through the map styles without lifting the tile-management state.
  useImperativeHandle(
    ref,
    () => ({
      cycleLayer() {
        const i = LAYER_ORDER.indexOf(activeLayer);
        switchLayer(LAYER_ORDER[(i + 1) % LAYER_ORDER.length]);
      },
      // Pan to an activity's coordinates (used when one is picked in the list).
      focusActivity(activity) {
        const map = mapRef.current;
        const lat = Number(activity?.values?.latitude);
        const lng = Number(activity?.values?.longitude);
        if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
        map.panTo([lat, lng], { animate: true });
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLayer]
  );

  const handleZoomIn = () => mapRef.current && mapRef.current.zoomIn();
  const handleZoomOut = () => mapRef.current && mapRef.current.zoomOut();

  const ready = phase === 'ready';

  return (
    <div className={`RouteMap RouteMap--${activeLayer}`}>
      <div ref={containerRef} className="RouteMap__canvas" aria-label="Mapa de rutas" />

      {/* Location gate — covers the map until the geolocation decision is made. */}
      {!ready && (
        <div className="RouteMap__gate">
          {phase === 'prompt' ? (
            <div className="RouteMap__gateCard">
              <span className="RouteMap__gateIcon" aria-hidden="true">
                <PinIcon />
              </span>
              <h3 className="RouteMap__gateTitle">Activa tu ubicación</h3>
              <p className="RouteMap__gateText">
                Para mostrarte las rutas cercanas necesitamos saber dónde estás. Si
                lo prefieres, puedes continuar y usaremos València como referencia.
              </p>
              <button
                type="button"
                className="RouteMap__gateBtn"
                onClick={requestLocation}
              >
                Permitir ubicación
              </button>
              <button
                type="button"
                className="RouteMap__gateSkip"
                onClick={skipLocation}
              >
                Continuar sin ubicación
              </button>
            </div>
          ) : (
            <div className="RouteMap__gateCard RouteMap__gateCard--loading">
              <span className="RouteMap__gateSpinner" aria-hidden="true" />
              <p className="RouteMap__gateText">
                {phase === 'locating' ? 'Localizando…' : 'Preparando el mapa…'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Controls only make sense once the map exists. */}
      {ready && (
        <>
          {/* Layer switch (top-left) */}
          <div className="RouteMap__layers" role="group" aria-label="Estilo de mapa">
            {LAYER_ORDER.map((key) => (
              <button
                key={key}
                type="button"
                className={`RouteMap__layer${activeLayer === key ? ' is-active' : ''}`}
                aria-pressed={activeLayer === key}
                onClick={() => switchLayer(key)}
              >
                {LAYERS[key].label}
              </button>
            ))}
          </div>

          {/* Zoom + recenter (top-right) */}
          <div className="RouteMap__controls">
            <div className="RouteMap__zoom">
              <button type="button" onClick={handleZoomIn} aria-label="Acercar">
                +
              </button>
              <span className="RouteMap__zoomSep" aria-hidden="true" />
              <button type="button" onClick={handleZoomOut} aria-label="Alejar">
                −
              </button>
            </div>
            <button
              type="button"
              className={`RouteMap__recenter${locating ? ' is-busy' : ''}`}
              onClick={recenter}
              aria-label="Centrar en mi ubicación"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <circle cx="12" cy="12" r="3.2" fill="currentColor" />
                <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="1.6" />
                <line x1="12" y1="1.5" x2="12" y2="5" stroke="currentColor" strokeWidth="1.6" />
                <line x1="12" y1="19" x2="12" y2="22.5" stroke="currentColor" strokeWidth="1.6" />
                <line x1="1.5" y1="12" x2="5" y2="12" stroke="currentColor" strokeWidth="1.6" />
                <line x1="19" y1="12" x2="22.5" y2="12" stroke="currentColor" strokeWidth="1.6" />
              </svg>
            </button>
          </div>
        </>
      )}
    </div>
  );
});

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
      <path
        d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.6" fill="currentColor" />
    </svg>
  );
}

export default RouteMap;
